import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LoginService } from '../login/login.service';
import { environment } from '../../environments/environment';

/**
 * Public lead onboarding / "sales will reach out" screen. Reached only via Google sign-in
 * for an email that does not belong to any account. State (credential + lead status) arrives
 * through router navigation state — never the URL — so the verified credential is not leaked
 * into browser history. The durable persistence guarantee (survives re-login / incognito)
 * comes from the server re-deriving the lead status on every Google sign-in, not from here.
 */
@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss'],
})
export class OnboardingComponent implements OnInit {
    credential: string | null = null;
    status: string = 'pending_onboarding';
    lead: any = null;
    onboardingForm!: FormGroup;
    isloading = false;
    errorMessage = '';
    currentYear = new Date().getFullYear();

    readonly pressroomCountOptions: string[] = [
        '1',
        '2-5',
        '6-10',
        'More than 10',
    ];

    readonly languageOptions: string[] = [
        'English',
        'Spanish',
        'French',
        'German',
        'Dutch',
        'Finnish',
        'Swedish',
        'Danish',
        'Norwegian',
        'Portuguese',
        'Japanese',
        'Chinese (Simplified)',
        'Chinese (Traditional)',
        'Other',
    ];

    constructor(
        private fb: FormBuilder,
        private route: Router,
        private loginService: LoginService,
    ) {
        const nav = this.route.getCurrentNavigation();
        const state = (nav?.extras?.state || history.state) as any;
        this.credential = state?.credential ?? null;
        this.status = state?.status ?? 'pending_onboarding';
        this.lead = state?.lead ?? null;
    }

    ngOnInit(): void {
        // Direct visit / refresh with no usable state and not already submitted -> back to login.
        if (!this.credential && this.status !== 'submitted') {
            this.route.navigate(['/login']);
            return;
        }

        this.onboardingForm = this.fb.group({
            firstName: [
                this.lead?.firstName || '',
                [Validators.required, Validators.maxLength(100)],
            ],
            lastName: [
                this.lead?.lastName || '',
                [Validators.required, Validators.maxLength(100)],
            ],
            organization: [
                this.lead?.organization || '',
                [Validators.required, Validators.maxLength(200)],
            ],
            numberOfPressrooms: [
                this.lead?.numberOfPressrooms || '',
                [Validators.required],
            ],
            pressroomLanguage: [
                this.lead?.pressroomLanguage || '',
                [Validators.required],
            ],
            alternateEmail: [
                this.lead?.alternateEmail || '',
                [Validators.email, Validators.maxLength(150)],
            ],
            phone: [this.lead?.phone || '', [Validators.maxLength(50)]],
            website: [this.lead?.website || '', [Validators.maxLength(300)]],
            message: [this.lead?.message || '', [Validators.maxLength(2000)]],
        });
    }

    submit(): void {
        if (this.onboardingForm.invalid || !this.credential) {
            this.onboardingForm.markAllAsTouched();
            return;
        }
        this.isloading = true;
        this.errorMessage = '';
        const profile = this.onboardingForm.getRawValue();
        this.loginService.googleOnboard(this.credential, profile).subscribe(
            (response: any) => {
                this.isloading = false;
                // An account appeared for this email in the meantime -> sign in normally.
                if (response?.type === 'user' && response.alreadyAccount) {
                    this.route.navigate(['/login']);
                    return;
                }
                this.status = 'submitted';
                this.lead = response?.lead ?? this.lead;
                // Notify sales only after the lead is saved. Browser-side (Formspree's
                // supported path); fire-and-forget so it can never affect the user.
                this.notifySalesViaFormspree(profile, this.lead?.email || '');
            },
            (err: any) => {
                this.isloading = false;
                this.errorMessage =
                    err?.error?.message ||
                    'Something went wrong. Please try again.';
            },
        );
    }

    // Sends the lead details to the sales inbox via Formspree. Uses native fetch (not
    // HttpClient) to bypass the auth interceptor, which would add an x-access-token header
    // and trigger a CORS preflight Formspree rejects. Fully non-blocking and best-effort.
    private notifySalesViaFormspree(profile: any, email: string): void {
        const endpoint = environment.formspreeEndpoint;
        if (!endpoint) {
            return;
        }
        try {
            fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    _subject: `New Blumepress lead: ${email}`,
                    email,
                    firstName: profile.firstName,
                    lastName: profile.lastName,
                    organization: profile.organization,
                    numberOfPressrooms: profile.numberOfPressrooms,
                    pressroomLanguage: profile.pressroomLanguage,
                    alternateEmail: profile.alternateEmail,
                    phone: profile.phone,
                    website: profile.website,
                    message: profile.message,
                }),
            }).catch(() => {
                // Best-effort: a notification failure must never affect the user.
            });
        } catch {
            // Ignore — notification is non-critical.
        }
    }

    goToLogin(): void {
        this.route.navigate(['/login']);
    }
}
